import { type Capture, type IdCallback } from '@canvas/input-types';
import { type CanvasHTMLElement } from '@canvas/input-types/base-event-types';
import { type KeyboardDownCapture } from '@canvas/input-types/keyboard-events-types';
import { ShortcutKeyMap } from '@canvas/shortcuts/keymap';
import { type OperatingSystem, type ShortcutModifier } from '@canvas/shortcuts/types';

import { getInputTarget } from '../input-target/get-input-target';

/**
 * On macOS, Option + a letter changes `evt.key` to a special character (e.g. Option+R → "®").
 * When Alt/Option is held and `code` is `KeyA`–`KeyZ`, return the base letter from `code`
 * so shortcut matching stays stable.
 */
export const normalizeKeyboardEventKey = (evt: KeyboardEvent): string => {
  if (evt.altKey && /^Key[A-Z]$/.test(evt.code)) {
    return evt.code.slice(3).toLowerCase();
  }
  return evt.key;
};

/**
 * Adds event listeners and massages our event data, adding the resulting data to a stream.
 * Returns a cleanup function for removing event listeners.
 */
export const setupKeyboardEvents = (
  root: CanvasHTMLElement,
  id: IdCallback,
  os: OperatingSystem,
  processKeyboardShortcuts: (
    capture: KeyboardDownCapture,
    preventDefault?: VoidFunction,
  ) => (() => void) | null,
  processCheatCodes: (capture: KeyboardDownCapture) => void,
  onCapture: (capture: Capture) => void,
  onModifierDown: (modifier: ShortcutModifier) => void,
  onModifierUp: (modifier: ShortcutModifier) => void,
  useCapturePhase = false,
): (() => void) => {
  const onKeyDown = (evt: KeyboardEvent) => {
    if (ShortcutKeyMap.isDOMKeyModifier(evt.key)) {
      onModifierDown(ShortcutKeyMap.fromDOMModifier(os, evt.key));
    }

    const rawKey = normalizeKeyboardEventKey(evt);
    const key = ShortcutKeyMap.fromDOMKey(rawKey);
    const target = getInputTarget(evt.target);

    const capture: KeyboardDownCapture = {
      id: id(),
      type: 'keyboardDown',
      target,
      properties: {
        // eg 'a' or '9'
        key,
        // eg 'KeyA' or 'Digit9'
        code: evt.code,
        // starts as false and can be overriden in the state machine
        consumed: false,
        // the raw DOM event
        event: evt,
      },
    };

    // Important that the keyboardDown event is processed first, to allow the shortcut
    // to be cancelled
    onCapture(capture);

    const shortcutCancelCallback = processKeyboardShortcuts(
      capture,
      evt.preventDefault.bind(evt),
    );
    if (shortcutCancelCallback) {
      capture.properties.cancelShortcut = shortcutCancelCallback;
    } else {
      // To avoid any cheat code clashing with keyboard shortcuts we only listen
      // for cheat codes if the shortcut hasn't been matched.
      processCheatCodes(capture);
    }
  };

  const onKeyUp = (evt: KeyboardEvent) => {
    if (ShortcutKeyMap.isDOMKeyModifier(evt.key)) {
      onModifierUp(ShortcutKeyMap.fromDOMModifier(os, evt.key));
    }

    const key = ShortcutKeyMap.fromDOMKey(evt.key);
    const target = getInputTarget(evt.target);

    onCapture({ id: id(), type: 'keyboardUp', target, properties: { key, event: evt } });
  };

  const onKeyPress = (evt: KeyboardEvent) => {
    const key = ShortcutKeyMap.fromDOMKey(evt.key);
    const target = getInputTarget(evt.target);

    onCapture({
      id: id(),
      type: 'keyboardPress',
      target,
      properties: { key, event: evt },
    });
  };

  root.addEventListener('keydown', onKeyDown, useCapturePhase);
  root.addEventListener('keypress', onKeyPress, useCapturePhase);
  root.addEventListener('keyup', onKeyUp, useCapturePhase);

  return () => {
    root.removeEventListener('keydown', onKeyDown, useCapturePhase);
    root.removeEventListener('keypress', onKeyPress, useCapturePhase);
    root.removeEventListener('keyup', onKeyUp, useCapturePhase);
  };
};
